import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useRouter } from '@/utils/navigation';
import { useSnapshot } from 'valtio';
import { Calendar, Eye, RefreshCw, X } from 'lucide-react';

import getOrders from '@/services/OrderService/getOrders';
import getOrder from '@/services/OrderService/getOrder';
import searchByTerm from '@/services/Search/searchByTerm';
import { JobCallStatus, Order, OrderProfanityCheckStatus, OrderStatus, SingleOrder, TagsFilterTypes } from '@/types/order';
import { Hit, isHitSourceProduct } from '@/types/search';
import store from '@/store';
import { formatDateFromStringWithHour, formatTimeAgoFromString } from '@/utils/formatDate';
import { getCompanyId } from '@/utils/getCompanyId'
import SearchBar, { SearchBarFilters } from '@/components/UiElements/SearchBar';
import { PagerBar, PaginationBar } from '@/components/UiElements/Pagination';
import Splash from '@/components/splash/Splash';
import { Button, ListInfoItem, ListRow } from '@/components/UiElements';
import Popover from '@/components/UiElements/PopOver';
import ImageWithLoader from '@/components/UiElements/ImageWithLoader';
import { closeNotif, startNotif } from '@/components/DynamicForm/util/notifier';
import ModalCloseButton from '@/components/Modal/ModalCloseButton';
import OrderDateFilterDialog from '@/components/Order/OrderDateFilter/OrderDateFilterDialog';
import {
    getDateFilterSubtitle,
    isOrderDateRange,
    OrderDateRange,
    toOrderDateApiFilter,
} from '@/components/Order/OrderDateFilter/dateRangePresets';

// One request per page view. The orders endpoint caps size at 100 server-side.
const perPage = 40;
const minSearchTermLength = 3;
const searchDebounceMs = 350;
const orderDateFilterStoragePrefix = 'order-list-created-date-filter';

interface CompanyDateFilterState {
    companyId: string | null;
    range: OrderDateRange | null;
}

const getOrderDateFilterStorageKey = (companyId: string) => (
    `${orderDateFilterStoragePrefix}:${companyId}`
);

const readStoredOrderDateFilter = (companyId: string | null): OrderDateRange | null => {
    if (!companyId || typeof window === 'undefined') return null;

    try {
        const storedValue = localStorage.getItem(getOrderDateFilterStorageKey(companyId));
        if (!storedValue) return null;

        const parsedValue: unknown = JSON.parse(storedValue);
        return isOrderDateRange(parsedValue) ? parsedValue : null;
    } catch {
        return null;
    }
};

const persistOrderDateFilter = (companyId: string, range: OrderDateRange | null) => {
    try {
        const storageKey = getOrderDateFilterStorageKey(companyId);

        if (range) {
            localStorage.setItem(storageKey, JSON.stringify(range));
        } else {
            localStorage.removeItem(storageKey);
        }
    } catch {
        // The active filter still works when browser storage is unavailable.
    }
};

const getOrderKey = (order: Order) => (
    order.id || order.order_number || `${order.external_order_number}-${order.created_at}`
);

// The search index stores a trimmed order document. It carries everything the
// list row needs except netsuite_status, which is not indexed — rows sourced
// from search fall back to the same "N/A" the list already renders for a
// missing status.
const mapSearchHitToOrder = (hit: Hit): Order | null => {
    const source = hit._source;

    if (!source || isHitSourceProduct(source)) return null;

    return {
        id: source.id,
        company_id: source.company_id,
        order_number: source.order_number,
        external_order_number: source.external_order_number,
        order_date: source.order_date,
        created_at: source.created_at,
        order_status: source.order_status as OrderStatus,
        lumialase_job_call_status: source.lumialase_job_call_status as JobCallStatus,
        netsuite_status: '',
    };
};

const hasPendingJob = (order: Order) => (
    order.lumialase_job_call_status === JobCallStatus.NOT_STARTED ||
    order.lumialase_job_call_status === JobCallStatus.FAILED
);

const hasPendingFulfillment = (order: Order) => order.order_status === OrderStatus.CREATED;

const getOrderAgeIndicatorClassName = (dateString?: string | null) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const elapsed = Date.now() - date.getTime();

    if (Number.isNaN(elapsed)) return null;

    const oneDay = 24 * 60 * 60 * 1000;
    const sevenDays = 7 * oneDay;

    if (elapsed < oneDay) return 'bg-primary-500';
    if (elapsed <= sevenDays) return 'bg-yellow-500';

    return 'bg-red-500';
};

const formatStatusValue = (value?: string | null) => value?.replace(/_/g, ' ') || null;
const formatStatusLabel = (value?: string | null) => (
    value
        ? value.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase())
        : 'N/A'
);
const getStatusIndicatorClassName = (value?: string | null) => {
    if (!value) return null;

    const status = value.toLowerCase();

    if (['done', 'accepted', 'successful', 'success', 'approved', 'completed', 'ready'].includes(status)) {
        return 'bg-primary-500';
    }

    if (['failed', 'rejected', 'cancelled', 'not_able_to_calculate', 'not_completed'].includes(status)) {
        return 'bg-red-500';
    }

    if (['requires_manual_check', 'pending_approval'].includes(status)) {
        return 'bg-yellow-500';
    }

    if (['created', 'marking', 'processing', 'in_progress'].includes(status)) {
        return 'bg-blue-500';
    }

    return 'bg-gray-400';
};

const OrderQuickView = ({ order, showJobStatus }: { order: Order; showJobStatus: boolean }) => {
    const [previewOrder, setPreviewOrder] = useState<SingleOrder | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewError, setPreviewError] = useState(false);
    const [activePreview, setActivePreview] = useState(1);

    const loadPreview = async () => {
        if (!order.id || loadingPreview || previewOrder) return;

        setLoadingPreview(true);
        setPreviewError(false);

        const result = await getOrder(order.id, 'id');

        if (result?.order) {
            setPreviewOrder(result.order);
            setActivePreview(1);
        } else {
            setPreviewError(true);
        }

        setLoadingPreview(false);
    };

    const lineItems = previewOrder?.order_details || [];
    const activeLineItem = lineItems[activePreview - 1];
    const activeSections = (activeLineItem?.sections || []).filter(section => section.snapshot);
    const activeProfanityStatus = String(activeLineItem?.profanity_check_status || '').toLowerCase();
    const isActivePendingApproval = activeLineItem ? ![
        OrderProfanityCheckStatus.ACCEPTED,
        OrderProfanityCheckStatus.REJECTED,
    ].includes(activeProfanityStatus as OrderProfanityCheckStatus) : false;

    return (
        <div className="self-center" onClick={(event) => event.stopPropagation()}>
            <Popover
                placement={['left center', 'left top', 'left bottom', 'right center', 'top right', 'bottom right']}
                offsetX={10}
                offsetY={0}
                toggle={(open) => (
                    <Button
                        type={open ? 'primary' : 'alt'}
                        size="sm"
                        icon={<Eye size={17}/>}
                        onClick={loadPreview}
                        className="min-w-[110px]"
                    >
                        Quick View
                    </Button>
                )}
                content={(close) => (
                    <div className="w-[560px] max-w-[calc(100vw-32px)] overflow-hidden">
                        <div className="flex items-center justify-between gap-md border-b border-gray-200 bg-white px-md py-sm">
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-900">
                                    {`Order #${previewOrder?.order_number || order.order_number}`}
                                </p>
                                {previewOrder && (
                                    <p className="text-xs text-gray-400">
                                        {`${lineItems.length} line item${lineItems.length === 1 ? '' : 's'}`}
                                    </p>
                                )}
                            </div>
                            <ModalCloseButton onClick={close}/>
                        </div>
                        {loadingPreview && (
                            <div className="p-lg min-h-[110px] flex flex-col justify-center gap-md">
                                <div className="flex items-center justify-between gap-md">
                                    <p className="text-sm font-medium text-gray-700">Loading order preview...</p>
                                    <span className="text-xs text-gray-400">#{order.order_number}</span>
                                </div>
                                <div className="h-[6px] w-full overflow-hidden rounded-full bg-gray-100">
                                    <div className="h-full w-2/3 rounded-full bg-primary-500 animate-pulse"/>
                                </div>
                            </div>
                        )}

                        {!loadingPreview && previewError && (
                            <div className="p-lg text-center">
                                <p className="text-sm text-red-600">Could not load this order.</p>
                                <button className="mt-sm text-sm font-medium text-primary-700 hover:text-primary-900" onClick={loadPreview}>
                                    Try again
                                </button>
                            </div>
                        )}

                        {!loadingPreview && previewOrder && (
                            <>
                                <div className="bg-gray-50">
                                    {activeLineItem ? (
                                        <section className="overflow-hidden rounded-md bg-white">
                                            <div className="bg-gray-100 p-[20px]">
                                                <div className="min-w-0 mb-sm">
                                                    <p className="truncate text-sm font-semibold text-gray-900">
                                                        {`SKU ${activeLineItem.sku}`}
                                                    </p>
                                                </div>

                                                <div className="flex flex-wrap gap-sm">
                                                    <div className="rounded-md bg-white px-sm py-xs min-w-[100px]">
                                                        <p className="text-[11px] text-gray-400">Item Status</p>
                                                        <p className="text-xs font-medium text-gray-700">{formatStatusLabel(activeLineItem.status)}</p>
                                                    </div>

                                                    {showJobStatus && (
                                                        <div className="rounded-md bg-white px-sm py-xs min-w-[100px]">
                                                            <p className="text-[11px] text-gray-400">Job Status</p>
                                                            <p className="text-xs font-medium text-gray-700">{formatStatusLabel(activeLineItem.lumialase_job_call_status)}</p>
                                                        </div>
                                                    )}

                                                    <div className={`rounded-md px-sm py-xs min-w-[112px] ${isActivePendingApproval ? 'bg-[#FEF0C7]' : 'bg-white'}`}>
                                                        <p className={`text-[11px] ${isActivePendingApproval ? 'text-[#B54708]' : 'text-gray-400'}`}>Profanity</p>
                                                        <p className={`text-xs font-medium ${isActivePendingApproval ? 'text-[#B54708]' : 'text-gray-700'}`}>
                                                            {isActivePendingApproval ? 'Pending Approval' : formatStatusLabel(activeLineItem.profanity_check_status)}
                                                        </p>
                                                    </div>

                                                    <div className="rounded-md bg-white px-sm py-xs min-w-[168px]">
                                                        <p className="text-[11px] text-gray-400">Progress</p>
                                                        <p className="text-xs font-medium text-gray-700 whitespace-nowrap">
                                                            <span className="text-[#027A48]">{activeLineItem.quantity_done}</span>
                                                            {` from ${activeLineItem.quantity} `}
                                                            <span className={activeLineItem.quantity_failed > 0 ? 'text-[#D92D20]' : 'text-gray-400'}>
                                                                {`(${activeLineItem.quantity_failed} Failed Attempt${activeLineItem.quantity_failed === 1 ? '' : 's'})`}
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {activeSections.length > 0 ? (
                                                <div className="grid grid-cols-4 gap-sm p-[20px]">
                                                    {activeSections.map((section, sectionIndex) => (
                                                        <figure key={`${section.name}-${section.bundle_index}-${sectionIndex}`} className="min-w-0 overflow-hidden">
                                                            <div className="flex aspect-square items-center justify-center">
                                                                <ImageWithLoader
                                                                    src={section.snapshot}
                                                                    alt={`${section.name} design`}
                                                                    className="!w-full !h-full"
                                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                                    cache
                                                                />
                                                            </div>
                                                            <figcaption className="mt-[5px] truncate text-center text-[11px] font-medium text-gray-700" title={section.name}>
                                                                {section.name}
                                                            </figcaption>
                                                        </figure>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="m-md flex h-[160px] items-center justify-center rounded-md bg-gray-50 text-xs text-gray-400">
                                                    No design preview available
                                                </div>
                                            )}
                                        </section>
                                    ) : (
                                        <div className="p-lg text-center text-sm text-gray-400">No line items found.</div>
                                    )}
                                </div>

                                <PaginationBar
                                    currentPage={activePreview}
                                    totalPages={lineItems.length}
                                    onPageChange={setActivePreview}
                                    label=""
                                    showNavigation
                                />
                            </>
                        )}
                    </div>
                )}
            />
        </div>
    );
};

const OrderScroll = () => {
    const [data, setData] = useState<Order[]>([]);
    const [tagsFilter, setTagsFilter] = useState<TagsFilterTypes[]>([]);
    const [searchText, setSearchText] = useState('');
    // Debounced copy of searchText. Empty means "browse mode" (orders endpoint);
    // non-empty means "search mode" (search service).
    const [activeSearchTerm, setActiveSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    // Only the search service reports a match total, so this stays undefined
    // while browsing.
    const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const router = useRouter()
    const snap = useSnapshot(store);
    const ordersRequestIdRef = useRef(0);

    // const companyId = sessionStorage.getItem('companyId')
    const companyId = getCompanyId()
    const normalizedCompanyId = companyId ? String(companyId) : null;
    const [dateFilterState, setDateFilterState] = useState<CompanyDateFilterState>(() => ({
        companyId: normalizedCompanyId,
        range: readStoredOrderDateFilter(normalizedCompanyId),
    }));
    const isDateFilterReady = dateFilterState.companyId === normalizedCompanyId;
    const appliedDateFilter = isDateFilterReady ? dateFilterState.range : null;
    const dateFilterSubtitle = getDateFilterSubtitle(appliedDateFilter);
    const hasNeatolabsMachine = snap.companySetting.machine === 'neatolabs';
    const erpIntegrationName = typeof snap.companySetting.erp_integration === 'string'
        ? snap.companySetting.erp_integration.trim()
        : '';

    useEffect(() => {
        if (dateFilterState.companyId === normalizedCompanyId) return;

        ordersRequestIdRef.current += 1;
        setDateFilterState({
            companyId: normalizedCompanyId,
            range: readStoredOrderDateFilter(normalizedCompanyId),
        });
        setIsDateFilterOpen(false);
        setData([]);
        setPage(1);
        setLoading(Boolean(normalizedCompanyId));
    }, [dateFilterState.companyId, normalizedCompanyId]);

    // Debounce typing so each keystroke does not hit the search service.
    useEffect(() => {
        const trimmedText = searchText.trim();
        const nextTerm = trimmedText.length >= minSearchTermLength ? trimmedText : '';

        if (nextTerm === activeSearchTerm) return;

        const timeoutId = setTimeout(() => {
            // Switching between browse and search changes the result set, so go
            // back to the first page in the same update — resetting in a separate
            // effect would fire one wasted request for the old page number.
            setActiveSearchTerm(nextTerm);
            setPage(1);
        }, searchDebounceMs);
        return () => clearTimeout(timeoutId);
    }, [activeSearchTerm, searchText]);

    const loadPage = useCallback(async () => {
        if (!companyId) {
            ordersRequestIdRef.current += 1;
            setData([]);
            setHasNextPage(false);
            setTotalPages(undefined);
            setLoading(false);
            return [];
        }

        if (!isDateFilterReady) return [];

        const requestId = ++ordersRequestIdRef.current;
        setLoading(true);
        setLoadError(null);

        try {
            if (activeSearchTerm) {
                // The search service pages natively and reports a real total, so
                // search mode can render exact page numbers.
                const { hits, total } = await searchByTerm(
                    activeSearchTerm,
                    { orders: true, products: false },
                    page,
                    perPage,
                );

                if (requestId !== ordersRequestIdRef.current) return [];

                const orders = hits
                    .map(mapSearchHitToOrder)
                    .filter((order): order is Order => order !== null);

                setData(orders);
                setTotalPages(total > 0 ? Math.ceil(total / perPage) : undefined);
                setHasNextPage(page * perPage < total);
                return orders;
            }

            const response: any = await getOrders(
                page,
                perPage,
                appliedDateFilter ? toOrderDateApiFilter(appliedDateFilter) : {},
            );

            if (requestId !== ordersRequestIdRef.current) return [];

            // getOrders swallows transport errors into null; an empty page comes
            // back as a real object with an empty contents array.
            if (!response) throw new Error('Orders request failed');

            const contents: Order[] = response?.contents || [];

            setData(contents);
            // The orders endpoint returns no total, so a full page is the only
            // signal that more may exist.
            setTotalPages(undefined);
            setHasNextPage(contents.length === perPage);
            return contents;
        } catch (error) {
            if (requestId !== ordersRequestIdRef.current) return [];

            console.error('Failed to load orders', error);
            setData([]);
            setHasNextPage(false);
            setTotalPages(undefined);
            setLoadError("We couldn't load orders right now. Please try again in a moment.");
            return [];
        } finally {
            if (requestId === ordersRequestIdRef.current) setLoading(false);
        }
    }, [activeSearchTerm, appliedDateFilter, companyId, isDateFilterReady, page]);

    const handleApplyDateFilter = useCallback((range: OrderDateRange | null) => {
        if (!normalizedCompanyId) return;

        persistOrderDateFilter(normalizedCompanyId, range);
        setDateFilterState({companyId: normalizedCompanyId, range});
        setPage(1);
        setIsDateFilterOpen(false);
    }, [normalizedCompanyId]);

    const handleClearDateFilter = useCallback(() => {
        if (!normalizedCompanyId) return;

        persistOrderDateFilter(normalizedCompanyId, null);
        setDateFilterState({companyId: normalizedCompanyId, range: null});
        setPage(1);
    }, [normalizedCompanyId]);

    const handleRefreshOrders = async () => {
        const existingOrderKeys = new Set(data.map(getOrderKey));
        const notificationId = startNotif('Refreshing orders...');

        try {
            const refreshedOrders = await loadPage();
            const newOrdersCount = refreshedOrders.filter(order => !existingOrderKeys.has(getOrderKey(order))).length;

            closeNotif(
                notificationId,
                newOrdersCount > 0
                    ? `${newOrdersCount} new order${newOrdersCount === 1 ? '' : 's'} found on this page.`
                    : 'No new orders found.',
                { type: newOrdersCount > 0 ? 'success' : 'info' },
            );
        } catch {
            closeNotif(notificationId, 'Failed to refresh orders.', { type: 'error' });
        }
    };

    const handlePageChange = useCallback((nextPage: number) => {
        setPage(nextPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        loadPage();
    }, [loadPage, snap?.selectedCompanyId]);

    const handleTagsFilterChange = useCallback((tag: TagsFilterTypes, selected: boolean) => {
        setTagsFilter((currentTags) => {
            if (selected) {
                return currentTags.includes(tag) ? currentTags : [...currentTags, tag];
            }

            return currentTags.filter((currentTag) => currentTag !== tag);
        });
    }, []);

    useEffect(() => {
        if (hasNeatolabsMachine) return;

        setTagsFilter((currentTags) => currentTags.filter((tag) => tag !== TagsFilterTypes.JOB));
    }, [hasNeatolabsMachine]);

    // Text search is served by the search service, so only the status chips are
    // applied here — and they narrow the current page, not the whole data set.
    const filteredData = useMemo(() => {
        let result = data;

        if (tagsFilter.length > 0) {
            result = result.filter(item => {
                let jobCallStatusMatch = true;
                let orderStatusMatch = true;

                if (tagsFilter.includes(TagsFilterTypes.JOB)) {
                    jobCallStatusMatch = hasPendingJob(item);
                }

                if (tagsFilter.includes(TagsFilterTypes.FULFILLMENT)) {
                    orderStatusMatch = hasPendingFulfillment(item);
                }

                return jobCallStatusMatch && orderStatusMatch;
            });
        }

        return result;
    }, [data, tagsFilter]);

    const orderSearchFilters = useMemo<SearchBarFilters[]>(() => {
        return [
            {
                label: 'Waiting For Fulfillment',
                onClick: (value: boolean) => handleTagsFilterChange(TagsFilterTypes.FULFILLMENT, value),
                count: data.filter(hasPendingFulfillment).length,
                default: tagsFilter.includes(TagsFilterTypes.FULFILLMENT),
            },
            ...(hasNeatolabsMachine ? [{
                label: 'Waiting For Job',
                onClick: (value: boolean) => handleTagsFilterChange(TagsFilterTypes.JOB, value),
                count: data.filter(hasPendingJob).length,
                default: tagsFilter.includes(TagsFilterTypes.JOB),
            }] : []),
        ];
    }, [data, tagsFilter, handleTagsFilterChange, hasNeatolabsMachine]);

    const isSearching = Boolean(activeSearchTerm);

    return (
        <div className="min-h-[100%] relative">
            <OrderDateFilterDialog
                isOpen={isDateFilterOpen}
                value={appliedDateFilter}
                onApply={handleApplyDateFilter}
                onClose={() => setIsDateFilterOpen(false)}
            />

            <div className={"sticky top-[48px] bg-white z-[10]"}>
                <div className={"h-[48px] absolute top-[-48px] w-full bg-white z-[-1]"}></div>
                <SearchBar
                    placeholder={"Search all orders by number, SKU, product..."}
                    filters={orderSearchFilters}
                    onChange={setSearchText}
                    searchText={searchText}
                    options={[
                        {
                            label: (
                                <span className="flex items-center">
                                    <span className="flex flex-col items-start leading-none">
                                        <span>Filter Date</span>
                                        {dateFilterSubtitle && (
                                            <span className={`mt-[4px] text-[10px] font-normal ${
                                                appliedDateFilter ? 'text-white/80' : 'text-gray-400'
                                            }`}>
                                                {dateFilterSubtitle}
                                            </span>
                                        )}
                                    </span>
                                    {appliedDateFilter && (
                                        <span
                                            className="ml-md flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/50"
                                            title="Clear date filter"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleClearDateFilter();
                                            }}
                                        >
                                            <X size={17}/>
                                        </span>
                                    )}
                                </span>
                            ),
                            icon: <Calendar size={17}/>,
                            type: appliedDateFilter ? 'primary' : 'alt',
                            className: dateFilterSubtitle ? 'min-w-[118px]' : undefined,
                            // The search service has no date filter, so the range
                            // cannot be combined with a search term.
                            disabled: isSearching,
                            onClick: () => setIsDateFilterOpen(true),
                        },
                        {
                            label: 'Refresh List',
                            icon: <RefreshCw size={17} className={loading ? 'animate-spin' : ''}/>,
                            onClick: handleRefreshOrders,
                            disabled: loading,
                        },
                    ]}
                />
            </div>

            {loading && <Splash />}

            {loadError && !loading && (
                <div className={"flex flex-col items-center gap-sm px-mx py-xl text-text-md text-gray-400 bg-gray-50 rounded-md mt-xxl text-center"}>
                    <span>{loadError}</span>
                    <Button type={"alt"} size={"sm"} onClick={() => loadPage()} disabled={loading}>
                        Retry
                    </Button>
                </div>
            )}

            {!loadError && filteredData.length > 0 && (
                <div className={"pagination-container flex flex-col pb-xxxl min-h-full pt-xxl"}>
                    <div className={"min-h-full flex-grow flex flex-col"}>
                        {filteredData.map((order: Order) => (
                        <ListRow
                            key={getOrderKey(order)}
                            title={<span className="block pl-4">{`Order #${order.order_number || 'N/A'}`}</span>}
                            className="!rounded-none !pb-4"
                            minHeightClassName="min-h-[96px]"
                            onClick={() => order.id && router.push(`/order/${order.id}`)}
                            actions={<OrderQuickView order={order} showJobStatus={hasNeatolabsMachine}/>}
                        >
                            <ListInfoItem
                                label="Order Status"
                                value={formatStatusValue(order.order_status)}
                                valueClassName="capitalize"
                                indicatorClassName={getStatusIndicatorClassName(order.order_status)}
                            />
                            {hasNeatolabsMachine && (
                                <ListInfoItem
                                    label="Job Status"
                                    value={formatStatusValue(order.lumialase_job_call_status)}
                                    valueClassName="capitalize"
                                    indicatorClassName={getStatusIndicatorClassName(order.lumialase_job_call_status)}
                                />
                            )}
                            {erpIntegrationName && (
                                <ListInfoItem
                                    label={`${erpIntegrationName} Status`}
                                    value={formatStatusValue(order.netsuite_status)}
                                    valueClassName="capitalize"
                                    indicatorClassName={getStatusIndicatorClassName(order.netsuite_status)}
                                />
                            )}
                            <ListInfoItem
                                label="Order Age"
                                value={formatTimeAgoFromString(order.order_date)}
                                indicatorClassName={getOrderAgeIndicatorClassName(order.order_date)}
                                className="ml-[10px]"
                            />
                            <ListInfoItem
                                label="Placed On"
                                value={formatDateFromStringWithHour(order.order_date)}
                                valueClassName="!text-gray-400"
                                className="min-w-[180px]"
                            />
                            <ListInfoItem
                                label="Received On"
                                value={formatDateFromStringWithHour(order.created_at)}
                                valueClassName="!text-gray-400"
                                className="min-w-[180px]"
                            />
                        </ListRow>
                        ))}
                    </div>

                    <PagerBar
                        currentPage={page}
                        totalPages={totalPages}
                        hasNext={hasNextPage}
                        onPageChange={handlePageChange}
                        disabled={loading}
                        className="sticky bottom-0"
                    />
                </div>
            )}

            {!loadError && !loading && filteredData.length === 0 && (
                <div className={"flex flex-col justify-center items-center px-mx py-xl text-gray-300 bg-gray-50 rounded-md mt-xxl gap-md text-center"}>
                    <p>
                        <span>No results match your current filters. Try </span>
                        <span
                            className={"text-gray-300 underline cursor-pointer hover:text-gray-900 transition-all"}
                            onClick={() => {
                                setSearchText('');
                                setActiveSearchTerm('');
                                setTagsFilter([]);
                                handleClearDateFilter();
                                setPage(1);
                            }}
                        >resetting some filters</span>
                        <span> to see more orders.</span>
                    </p>
                    {page > 1 && (
                        <Button type={"alt"} size={"sm"} onClick={() => handlePageChange(page - 1)}>
                            {`Back to page ${page - 1}`}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

export default OrderScroll;
